import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api/axios"; 
import PublicNavbar from "../components/PublicNavbar";
import { Eye, EyeOff, Loader2, ShieldCheck, AlertTriangle, Check } from "lucide-react";

const colors = {
  primary: "#03f8d7",
  accent: "#ff2d75",
  deepBg: "#060608",
  surface: "rgba(13, 13, 18, 0.95)",
  textMain: "#ffffff",
  textMuted: "#64748b",
  border: "rgba(255, 255, 255, 0.08)",
};

function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showRedirect, setShowRedirect] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  
  // LIGHT FUNCTION STATE
  const [activeField, setActiveField] = useState(null);
  const isLightOn = activeField === "email"; // Light only triggers for email/username

  // Load remembered user on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem("remembered_station_email");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 4000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    if (!email || !password) {
      setError("REQUIRED: ENTER ALL CREDENTIALS");
      return;
    }

    try {
      setLoading(true);
      setError("");

      if (rememberMe) {
        localStorage.setItem("remembered_station_email", email);
      } else {
        localStorage.removeItem("remembered_station_email");
      }

      // FIX: Using the full URL to stop the "double /api" issue
      // Inside handleLogin in Login.jsx:
      const res = await api.post("/api/auth/login", { email, password });      
      
      // Safe check to avoid undefined crashes
      const userRole = res.data?.user?.role || res.data?.role;

      if (userRole === "admin") {
        const secret = res.data.adminSecret || res.data.token; 
        localStorage.setItem("adminToken", secret);
        localStorage.setItem("admin-secret", secret);
        localStorage.setItem("adminId", res.data.user._id);
      }

      setShowRedirect(true);

      setTimeout(() => {
        login(res.data); 
        navigate(userRole === "admin" ? "/admin/dashboard" : "/");
      }, 2000);

    } catch (err) {
      console.error("Login Error:", err);
      setError(err.response?.data?.message?.toUpperCase() || "ACCESS DENIED: VERIFY CONNECTION");
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <style>
        {`
          .login-page {
            min-height: 100vh;
            background-color: ${colors.deepBg};
            display: flex;
            flex-direction: column;
            font-family: 'Inter', sans-serif;
          }
          .content-wrapper {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
          }
          .login-card {
            width: 100%;
            max-width: 420px;
            background: ${colors.surface};
            padding: 40px 40px 50px 40px;
            border-radius: 28px;
            border: 1px solid ${colors.border};
            box-shadow: 0 40px 100px rgba(0,0,0,0.8);
            text-align: center;
            position: relative;
            overflow: hidden;
          }
          .card-bg-logo {
            position: absolute;
            top: 50%; left: 50%;
            transform: translate(-50%, -50%);
            width: 85%; opacity: 0.1;
            filter: blur(10px); pointer-events: none; z-index: 0;
          }

          /* LIGHT EFFECTS FROM SIGNUP */
          .card-light-strip {
            position: absolute;
            top: 0; left: 0; right: 0;
            height: 3px;
            background: ${colors.primary};
            box-shadow: 0 0 20px ${colors.primary};
            opacity: ${isLightOn ? '1' : '0'};
            transition: opacity 0.3s ease;
            z-index: 3;
          }
          .card-light-beam {
            position: absolute;
            top: 0; left: 12%; right: 12%;
            height: 250px;
            background: linear-gradient(to bottom, rgba(3, 248, 215, 0.12), transparent);
            clip-path: polygon(15% 0%, 85% 0%, 100% 100%, 0% 100%);
            opacity: ${isLightOn ? '1' : '0'};
            transition: opacity 0.5s ease;
            pointer-events: none;
            z-index: 1;
          }

          .login-title {
            position: relative; z-index: 2;
            font-size: 20px; font-weight: 800; letter-spacing: 4px;
            margin-bottom: 45px; text-transform: uppercase; color: #fff;
            opacity: ${isLightOn ? '0' : '1'};
            transform: translateY(${isLightOn ? '-20px' : '0'});
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .input-group { position: relative; margin-bottom: 25px; text-align: left; z-index: 2; }
          .input-field {
            width: 100%; background: rgba(255, 255, 255, 0.03);
            border: 1px solid ${colors.border}; border-radius: 14px;
            padding: 18px 20px; color: white; font-size: 15px;
            outline: none; transition: 0.3s; box-sizing: border-box;
          }
          .input-field:focus { 
            border-color: ${isLightOn ? colors.primary : colors.textMuted}; 
          }
          .label-text {
            position: absolute; left: 20px; top: 50%;
            transform: translateY(-50%); color: ${colors.textMuted};
            pointer-events: none; transition: 0.3s;
          }
          .input-field:focus + .label-text,
          .input-field:not(:placeholder-shown) + .label-text {
            top: -12px; left: 5px; font-size: 11px;
            color: ${colors.primary}; font-weight: 700; text-transform: uppercase;
          }
          .remember-container {
            display: flex; align-items: center; gap: 10px;
            margin-bottom: 25px; cursor: pointer; user-select: none;
            z-index: 2; position: relative; padding-left: 5px;
          }
          .custom-checkbox {
            width: 20px; height: 20px; border: 1px solid ${colors.border};
            border-radius: 6px; display: flex; align-items: center;
            justify-content: center; transition: 0.2s;
            background: rgba(255,255,255,0.05);
          }
          .custom-checkbox.active { background: ${colors.primary}; border-color: ${colors.primary}; }
          .remember-text { color: ${colors.textMuted}; font-size: 13px; font-weight: 600; letter-spacing: 1px; }

          .login-btn {
            width: 100%; padding: 18px; background: ${colors.primary};
            color: #000; border: none; border-radius: 14px;
            font-weight: 900; text-transform: uppercase; letter-spacing: 2px;
            cursor: pointer; position: relative; z-index: 2;
          }
          .login-btn:disabled { opacity: 0.6; cursor: not-allowed; }

          .error-toast {
            background: rgba(255, 45, 117, 0.15); border: 1px solid ${colors.accent};
            color: ${colors.accent}; padding: 12px; border-radius: 10px;
            margin-bottom: 20px; font-size: 12px; font-weight: 800;
            display: flex; align-items: center; justify-content: center; gap: 8px;
            z-index: 5; position: relative;
          }
          .footer-nav {
            display: flex; justify-content: space-between; margin-top: 25px;
            font-size: 13px; z-index: 2; position: relative;
          }
          .nav-link { color: ${colors.textMuted}; cursor: pointer; }
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          .animate-spin { animation: spin 1s linear infinite; }
        `}
      </style>

      <PublicNavbar />

      <div className="content-wrapper">
        <div className="login-card">
          {/* LIGHT ELEMENTS INJECTED */}
          <div className="card-light-strip" />
          <div className="card-light-beam" />
          
          <img src="/loginbg.png" alt="logo" className="card-bg-logo" />
          <h1 className="login-title">LOGIN PANEL</h1>

          {error && (
            <div className="error-toast">
              <AlertTriangle size={14} />
              {error}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div className="input-group">
              <input
                className="input-field"
                type="text"
                placeholder=" "
                value={email}
                onFocus={() => setActiveField('email')}
                onBlur={() => setActiveField(null)}
                onChange={(e) => setEmail(e.target.value)}
              />
              <label className="label-text">Email or Username</label>
            </div>

            <div className="input-group">
              <input
                className="input-field"
                type={showPassword ? "text" : "password"}
                placeholder=" "
                value={password}
                onFocus={() => setActiveField('password')}
                onBlur={() => setActiveField(null)}
                onChange={(e) => setPassword(e.target.value)}
              />
              <label className="label-text">Password</label>
              <div 
                style={{position: 'absolute', right: '15px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: colors.textMuted}}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </div>
            </div>

            <div className="remember-container" onClick={() => setRememberMe(!rememberMe)}>
              <div className={`custom-checkbox ${rememberMe ? 'active' : ''}`}>
                {rememberMe && <Check size={10} color="#000" strokeWidth={3} />}
              </div>
              <span className="remember-text">REMEMBER ME</span>
            </div>

            <button className="login-btn" type="submit" disabled={loading}>
              {loading ? "INITIALIZING..." : "PROCEED TO LOGIN"}
            </button>
          </form>

          <div className="footer-nav">
            <span className="nav-link" onClick={() => setShowForgotModal(true)}>Forgot Password?</span>
            <span className="nav-link" style={{color: '#bbbbbb', fontWeight: '300'}} onClick={() => navigate("/signup")}>Register Now</span>
          </div>
        </div>
      </div>

      {showForgotModal && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(10px)'}}>
          <div style={{ background: '#0a0a0c', padding: '40px', borderRadius: '24px', border: `1px solid ${colors.border}`, textAlign: 'center', maxWidth: '380px' }}>
            <h3 style={{ color: colors.primary, marginBottom: '15px' }}>RECOVERY</h3>
            <p style={{ color: colors.textMuted, fontSize: '14px' }}>Contact support to reset terminal credentials.</p>
            <button onClick={() => setShowForgotModal(false)} style={{ background: colors.primary, border: 'none', padding: '12px 30px', borderRadius: '10px', marginTop: '20px', fontWeight: '800', cursor: 'pointer' }}>CLOSE</button>
          </div>
        </div>
      )}

      {showRedirect && (
        <div style={{position: 'fixed', inset: 0, background: colors.deepBg, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
          <div style={{ textAlign: 'center' }}>
            <ShieldCheck size={70} color={colors.primary} style={{ marginBottom: '20px' }} />
            <h2 style={{ letterSpacing: '4px', color: '#fff' }}>ACCESS GRANTED</h2>
            <Loader2 className="animate-spin" style={{ margin: '20px auto', color: colors.primary }} />
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
